import { useState, useRef, useCallback, useEffect } from 'react'
import { StorageKeys } from '@/lib/storage'
import { resolveChatModelId } from '@/lib/chat-model-display'
import type { Message, DocumentReference, ProposedEditData, QueuedChatMessage } from './chat-pane.types'
import type { ContentEditableChatInputHandle } from '@/components/ui/contenteditable-chat-input'
import { documentReferenceChipPresentInEditor } from '@/components/ui/contenteditable-chat-input.utils'
import { getConversation, fetchWithAuth } from '@/lib/api'
import { isAuthConfigured } from '@/lib/supabase'
import { useAuth } from '@/providers/auth-provider'
import { useWorkspace } from '@/providers/workspace-provider'

/**
 * Hook to manage all ChatPane state
 */
export function useChatPaneState() {
  // Legacy state for ingestion conversations (polling mode)
  const [legacyMessages, setLegacyMessages] = useState<Message[]>([])
  const [legacyInput, setLegacyInput] = useState("")
  const [showHistory, setShowHistory] = useState(false)
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0)
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null)
  const [loadingConversation, setLoadingConversation] = useState(false)
  const [isPolling, setIsPolling] = useState(false)
  const [activeConversations, setActiveConversations] = useState<Array<{
    id: string;
    title: string;
    message_count?: number;
    updated_at?: string;
    created_at?: string;
    status?: string;
    metadata?: Record<string, unknown>;
  }>>([])
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const pollingIntervalRef = useRef<number | null>(null)
  const deletingConversationsRef = useRef<Set<string>>(new Set())

  // Streaming chat state (manual implementation for now)
  const [streamingMessages, setStreamingMessages] = useState<Message[]>([])
  const [streamingInput, setStreamingInput] = useState("")
  const [isStreaming, setIsStreaming] = useState(false)
  const [isTyping, setIsTyping] = useState(false)
  const [copiedCode, setCopiedCode] = useState<string | null>(null)
  const [streamingConversationId, setStreamingConversationId] = useState<string | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)
  /** True from send commit through stream end; set before persist so new-* promote cannot wipe the user bubble. */
  const streamInFlightRef = useRef(false)
  /** Bumped on New chat / conversation switch so a detached stream cannot mutate the pane. */
  const streamEpochRef = useRef(0)

  // Diff preview state
  const [diffPreviewOpen, setDiffPreviewOpen] = useState(false)
  const [proposedEdit, setProposedEdit] = useState<ProposedEditData | null>(null)
  const [isApplyingEdit, setIsApplyingEdit] = useState(false)

  // LLM Provider & Model Selection State
  const [selectedProvider, setSelectedProvider] = useState<'openai' | 'anthropic' | 'groq' | 'xai' | 'localai' | 'ollama'>(() => {
    try {
      const saved = localStorage.getItem(StorageKeys.AI_SELECTED_PROVIDER)
      if (saved === 'openai' || saved === 'anthropic' || saved === 'groq' || saved === 'xai' || saved === 'localai' || saved === 'ollama') {
        return saved
      }
    } catch {
      /* ignore */
    }
    return 'anthropic'
  })
  const [selectedModel, setSelectedModel] = useState(() => {
    try {
      const saved = localStorage.getItem(StorageKeys.AI_LAST_SELECTED_MODEL)
      if (saved) return saved
    } catch {
      /* ignore */
    }
    return 'claude-sonnet-5-5'
  })
  const [availableModels, setAvailableModels] = useState<any[]>([])
  const [providersAvailable, setProvidersAvailable] = useState<any[]>([])
  const [allModelsByProvider, setAllModelsByProvider] = useState<Record<string, any[]>>({})

  // Auto-scroll control
  const [userHasScrolled, setUserHasScrolled] = useState(false)
  const [isNearBottom, setIsNearBottom] = useState(true)

  // Copy message state
  const contentEditableInputRef = useRef<ContentEditableChatInputHandle>(null)
  const prevReferencesRef = useRef<DocumentReference[]>([])

  // Edit message state
  const [editingMessageIndex, setEditingMessageIndex] = useState<number | null>(null)
  const [editContent, setEditContent] = useState<string>("")

  // Queued messages — submitted while a stream is in flight
  const [queuedMessages, setQueuedMessages] = useState<QueuedChatMessage[]>([])

  // Thinking mode state
  const [thinkingModeActive, setThinkingModeActive] = useState(false)
  const [thinkingOperations, setThinkingOperations] = useState<Array<{
    id: string
    type: 'search' | 'search_files' | 'read' | 'mcp_tool' | 'tool_call' | 'persona'
    label: string
    status: 'active' | 'completed' | 'error'
    result?: string
    timestamp?: string
    personaName?: string
    personaHint?: string
  }>>([])
  const [currentTask, setCurrentTask] = useState<{
    action: string
    /** Internal storage path (e.g. ws/{orgId}/{slug}.md) — never render directly; use displayPath. */
    filePath: string
    /** Human-facing "{workspaceSlug}/{docSlug}" path from the backend, when available. */
    displayPath?: string | null
    /** Full shareable webapp URL, when available. */
    url?: string | null
    content: string
    isWriting: boolean
    timestamp?: string
  } | null>(null)
  const [completedFiles, setCompletedFiles] = useState<Array<{
    filePath: string
    displayPath?: string | null
    url?: string | null
    status: 'new' | 'modified'
    linesAdded: number
    linesRemoved: number
    documentId?: string
    timestamp?: string
    title?: string
    changeStatus?: string
  }>>([])

  const [agenticRound, setAgenticRound] = useState<{ current: number; max: number } | null>(null)

  // Modified files bar above prompt (collapsible panel)
  const [modifiedFilesPanelExpanded, setModifiedFilesPanelExpanded] = useState(false)

  // Computed values
  const messages = currentConversationId ? legacyMessages : streamingMessages
  const input = currentConversationId ? legacyInput : streamingInput
  const isLoading = isPolling || isStreaming

  return {
    // Legacy state
    legacyMessages,
    setLegacyMessages,
    legacyInput,
    setLegacyInput,
    showHistory,
    setShowHistory,
    historyRefreshKey,
    setHistoryRefreshKey,
    currentConversationId,
    setCurrentConversationId,
    loadingConversation,
    setLoadingConversation,
    isPolling,
    setIsPolling,
    activeConversations,
    setActiveConversations,
    messagesContainerRef,
    pollingIntervalRef,
    deletingConversationsRef,

    // Streaming state
    streamingMessages,
    setStreamingMessages,
    streamingInput,
    setStreamingInput,
    isStreaming,
    setIsStreaming,
    isTyping,
    setIsTyping,
    copiedCode,
    setCopiedCode,
    streamingConversationId,
    setStreamingConversationId,
    abortControllerRef,
    streamInFlightRef,
    streamEpochRef,

    // Diff preview state
    diffPreviewOpen,
    setDiffPreviewOpen,
    proposedEdit,
    setProposedEdit,
    isApplyingEdit,
    setIsApplyingEdit,

    // LLM state
    selectedProvider,
    setSelectedProvider,
    selectedModel,
    setSelectedModel,
    availableModels,
    setAvailableModels,
    providersAvailable,
    setProvidersAvailable,
    allModelsByProvider,
    setAllModelsByProvider,

    // Auto-scroll
    userHasScrolled,
    setUserHasScrolled,
    isNearBottom,
    setIsNearBottom,

    // Refs
    contentEditableInputRef,
    prevReferencesRef,

    // Edit state
    editingMessageIndex,
    setEditingMessageIndex,
    editContent,
    setEditContent,

    // Message queue
    queuedMessages,
    setQueuedMessages,

    // Thinking mode
    thinkingModeActive,
    setThinkingModeActive,
    thinkingOperations,
    setThinkingOperations,
    currentTask,
    setCurrentTask,
    completedFiles,
    setCompletedFiles,
    agenticRound,
    setAgenticRound,

    // Modified files bar above prompt
    modifiedFilesPanelExpanded,
    setModifiedFilesPanelExpanded,

    // Computed
    messages,
    input,
    isLoading,
  }
}

/**
 * Hook for drag & drop functionality
 */
export function useDragAndDrop(
  contentEditableInputRef: React.RefObject<ContentEditableChatInputHandle | null>,
  isOver: boolean
) {
  // Handle @dnd-kit drop events (when item is dropped on chat input)
  useEffect(() => {
    if (isOver && contentEditableInputRef.current) {
      // Focus input when dragging over it to ensure cursor is available
      const editorElement = contentEditableInputRef.current as any
      if (editorElement?.editorRef?.current) {
        editorElement.editorRef.current.focus()
      }
    }
  }, [isOver, contentEditableInputRef])
}

/**
 * Hook for reference synchronization
 */
export function useReferenceSync(
  references: DocumentReference[],
  contentEditableInputRef: React.RefObject<ContentEditableChatInputHandle | null>,
  prevReferencesRef: React.MutableRefObject<DocumentReference[]>
) {
  // Detect when new references are added and insert them at cursor
  useEffect(() => {
    // Only insert chips if contentEditable input exists (new chat mode)
    // Legacy mode uses textarea which doesn't support inline chips
    // Check if contentEditable input exists rather than checking currentConversationId
    // This ensures drag & drop works in all modes where contentEditable is available
    if (!contentEditableInputRef.current?.insertReference) return

    const prevRefs = prevReferencesRef.current
    // Match by ID + line range so multiple refs to same doc (different line ranges) all get inserted
    const newRefs = references.filter(ref => !prevRefs.find(r => {
      if (r.id !== ref.id) return false
      return r.lineStart === ref.lineStart && r.lineEnd === ref.lineEnd
    }))

    if (newRefs.length > 0) {
      // Do not focus() here - it resets the selection and breaks inline @mention insert.
      // insertReference skips refs whose chip is already in the editor DOM.
      setTimeout(() => {
        const editorRoot = contentEditableInputRef.current?.editorRef?.current ?? null
        const insert = contentEditableInputRef.current?.insertReference
        if (!insert) return
        for (const ref of newRefs) {
          if (editorRoot && documentReferenceChipPresentInEditor(editorRoot, ref)) continue
          insert(ref)
        }
      }, 10)
    }

    prevReferencesRef.current = references
  }, [references, contentEditableInputRef, prevReferencesRef])
}

/**
 * Hook for LLM provider and model management
 */
export function useLLMProvider(
  selectedProvider: 'openai' | 'anthropic' | 'groq' | 'xai' | 'localai' | 'ollama',
  setSelectedProvider: (provider: 'openai' | 'anthropic' | 'groq' | 'xai' | 'localai' | 'ollama') => void,
  selectedModel: string,
  setSelectedModel: (model: string) => void,
  setAvailableModels: (models: any[]) => void,
  setProvidersAvailable: (providers: any[]) => void,
  setAllModelsByProvider: (models: Record<string, any[]>) => void,
  allModelsByProvider: Record<string, any[]>,
  availableModels: any[]
) {
  const { session, isLoading: authLoading } = useAuth()
  const { activeWorkspaceIds } = useWorkspace()

  // Helper to fetch models for a specific provider (returns models array)
  const fetchModelsForProvider = useCallback(async (provider: string): Promise<any[]> => {
    try {
      const qs = new URLSearchParams({ provider })
      if (activeWorkspaceIds?.[0]) qs.set('organizationId', activeWorkspaceIds[0])
      const response = await fetchWithAuth(`/api/llm/models?${qs.toString()}`)
      if (response.status === 401) {
        return []
      }
      const data = await response.json()

      if (data.success && data.models) {
        console.log(`[ChatPane] Available models for ${provider}:`, data.models)
        return data.models
      }
      return []
    } catch (error) {
      console.warn(`[ChatPane] Failed to fetch models for ${provider}:`, error)
      return []
    }
  }, [activeWorkspaceIds?.[0]])

  // Handler for provider selection change
  const handleProviderChange = useCallback((value: string) => {
    const provider = value as 'openai' | 'anthropic' | 'groq' | 'xai' | 'localai' | 'ollama'
    setSelectedProvider(provider)

    // Reset model to first available model for new provider
    const providerModels = allModelsByProvider[provider] || []
    if (providerModels.length > 0) {
      const firstModel = providerModels[0]
      setSelectedModel(firstModel.id || firstModel.name)
      // Save model selection using StorageKeys
      try {
        localStorage.setItem(StorageKeys.AI_SELECTED_MODEL(provider), firstModel.id || firstModel.name)
      } catch (error) {
        console.warn('[ChatPane] Failed to save model selection:', error)
      }
    }
  }, [allModelsByProvider, setSelectedProvider, setSelectedModel])

  // Load settings and fetch all available models after Supabase session is ready
  useEffect(() => {
    if (authLoading) return
    if (isAuthConfigured && !session?.access_token) return

    const loadSettingsAndModels = async () => {
      try {
        // 1. Load user settings to get preferred provider/model
        const settingsResponse = await fetchWithAuth(`/api/settings`)
        if (settingsResponse.status === 401) {
          return
        }
        const settingsData = await settingsResponse.json()

        let preferredProvider = selectedProvider
        let preferredModel = selectedModel

        try {
          const savedProvider = localStorage.getItem(StorageKeys.AI_SELECTED_PROVIDER)
          if (
            savedProvider === 'openai' ||
            savedProvider === 'anthropic' ||
            savedProvider === 'groq' ||
            savedProvider === 'xai' ||
            savedProvider === 'localai' ||
            savedProvider === 'ollama'
          ) {
            preferredProvider = savedProvider
          }
          const savedModel = localStorage.getItem(StorageKeys.AI_LAST_SELECTED_MODEL)
          if (savedModel) {
            preferredModel = savedModel
          }
        } catch (error) {
          console.warn('[ChatPane] Failed to load saved model/provider:', error)
        }

        if (!preferredModel && settingsData.success && settingsData.settings?.llm) {
          preferredProvider = settingsData.settings.llm.chatProvider || preferredProvider
          preferredModel = settingsData.settings.llm.defaultModel || 'claude-sonnet-5-5'
          console.log('[ChatPane] No local model — using server fallback:', { preferredProvider, preferredModel })
        } else if (settingsData.success && settingsData.settings?.llm && !localStorage.getItem(StorageKeys.AI_LAST_SELECTED_MODEL)) {
          preferredProvider = settingsData.settings.llm.chatProvider || preferredProvider
          preferredModel = settingsData.settings.llm.defaultModel || preferredModel
          console.log('[ChatPane] Loaded settings:', { preferredProvider, preferredModel })
        }

        setSelectedProvider(preferredProvider as any)
        if (preferredModel) {
          setSelectedModel(preferredModel)
        }

        // 2. Fetch available providers for active workspace (allow-list = keys on that org)
        const orgQs = activeWorkspaceIds?.[0]
          ? `?organizationId=${encodeURIComponent(activeWorkspaceIds[0])}`
          : ''
        const providersResponse = await fetchWithAuth(`/api/llm/providers${orgQs}`)
        if (!providersResponse.ok) {
          return
        }
        const providersData = await providersResponse.json()

        if (providersData.success) {
          console.log('[ChatPane] Available providers:', providersData.providers)
          if (
            !preferredModel &&
            providersData.llmPolicy?.defaultModel
          ) {
            preferredModel = providersData.llmPolicy.defaultModel
            if (providersData.llmPolicy.defaultProvider) {
              preferredProvider = providersData.llmPolicy.defaultProvider
            }
          }
          // Platform keys (Railway env or workspace secret) put a provider in the picker.
          const enabledProviderIds = providersData.providers
            .filter((p: any) => p.available)
            .map((p: any) => p.id)
          const providersWithEnabled = providersData.providers.map((p: any) => ({
            ...p,
            enabled: Boolean(p.available),
          }))
          setProvidersAvailable(providersWithEnabled)

          const modelsByProvider: Record<string, any[]> = {}

          for (const providerId of enabledProviderIds) {
            try {
              const models = await fetchModelsForProvider(providerId)
              if (models.length > 0) {
                modelsByProvider[providerId] = models
              }
            } catch (error) {
              console.error(`[ChatPane] Failed to fetch models for ${providerId}:`, error)
            }
          }

          setAllModelsByProvider(modelsByProvider)

          // 4. Flatten all models into a single list for the dropdown
          const allModels = Object.values(modelsByProvider).flat()
          setAvailableModels(allModels)

          // 5. Reconcile selection against loaded models (invalid ids leave Radix Select blank)
          const resolved = resolveChatModelId(preferredModel, allModels, modelsByProvider)
          if (resolved) {
            setSelectedModel(resolved)
          }
        }
      } catch (error) {
        console.warn('[ChatPane] Failed to load settings/models:', error)
      }
    }

    loadSettingsAndModels()
  }, [authLoading, session?.access_token, activeWorkspaceIds?.[0]]) // eslint-disable-line react-hooks/exhaustive-deps

  const persistModelSelection = useCallback((modelId: string, provider?: string) => {
    try {
      localStorage.setItem(StorageKeys.AI_LAST_SELECTED_MODEL, modelId)
      if (provider) {
        localStorage.setItem(StorageKeys.AI_SELECTED_PROVIDER, provider)
        localStorage.setItem(StorageKeys.AI_SELECTED_MODEL(provider), modelId)
      }
    } catch (error) {
      console.warn('[ChatPane] Failed to save model selection:', error)
    }
  }, [])

  // Sync provider when selected model changes (e.g. user picks GPT-4o while provider is 'anthropic')
  useEffect(() => {
    if (selectedModel && availableModels.length > 0) {
      const model = availableModels.find((m: any) => m.id === selectedModel)
      if (model && model.provider && model.provider !== selectedProvider) {
        console.log('[ChatPane] Model provider mismatch: model belongs to', model.provider, 'but provider is', selectedProvider, '— auto-correcting')
        setSelectedProvider(model.provider as any)
      }
      if (model?.provider) {
        persistModelSelection(selectedModel, model.provider)
      }
    }
  }, [selectedModel, availableModels, selectedProvider, setSelectedProvider, persistModelSelection])

  // Keep selectedModel valid when the model list refreshes (e.g. Anthropic API discovery)
  useEffect(() => {
    if (availableModels.length === 0 && Object.keys(allModelsByProvider).length === 0) return
    const resolved = resolveChatModelId(selectedModel, availableModels, allModelsByProvider)
    if (resolved && resolved !== selectedModel) {
      setSelectedModel(resolved)
    }
  }, [availableModels, allModelsByProvider, selectedModel, setSelectedModel])

  const handleModelChange = useCallback((value: string) => {
    setSelectedModel(value)
    const model = availableModels.find((m: any) => m.id === value)
    const provider = model?.provider as typeof selectedProvider | undefined
    if (provider && provider !== selectedProvider) {
      console.log('[ChatPane] Switching provider to', provider, 'for model', value)
      setSelectedProvider(provider)
    }
    persistModelSelection(value, provider ?? selectedProvider)
  }, [availableModels, selectedProvider, setSelectedModel, setSelectedProvider, persistModelSelection])

  return {
    fetchModelsForProvider,
    handleProviderChange,
    handleModelChange,
  }
}

/**
 * Hook for conversation polling (legacy ingestion mode)
 */
export function useConversationPolling(
  setIsPolling: (polling: boolean) => void,
  pollingIntervalRef: React.MutableRefObject<number | null>,
  messagesContainerRef: React.RefObject<HTMLDivElement | null>,
  setLegacyMessages: (messages: Message[]) => void,
  isNearBottom: boolean,
  userHasScrolled: boolean,
  setThinkingModeActive?: (active: boolean) => void,
  setIsTyping?: (typing: boolean) => void,
  setHistoryRefreshKey?: React.Dispatch<React.SetStateAction<number>>,
) {
  const dispatchedIngestionCompleteRef = useRef<Set<string>>(new Set());

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setIsPolling(false);
  }, [setIsPolling, pollingIntervalRef]);

  const startPolling = useCallback((conversationId: string, fastMode = false) => {
    console.log('[ChatPane] Starting polling:', conversationId, fastMode ? 'FAST (500ms)' : 'NORMAL (2000ms)');
    setIsPolling(true);

    // Clear any existing interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    // Fast mode: 500ms for active ingestion
    // Normal mode: 2000ms for regular conversations
    const interval = fastMode ? 500 : 2000;
    let consecutiveErrors = 0;
    const MAX_CONSECUTIVE_ERRORS = 5;

    pollingIntervalRef.current = window.setInterval(async () => {
      try {
        const result = await getConversation(conversationId, true);
        consecutiveErrors = 0;
        const conversation = result.conversation;

        // Update legacy messages (for ingestion conversations)
        const chatMessages: Message[] = (conversation.messages || []).map(msg => ({
          role: msg.role as "user" | "assistant",
          content: msg.content,
          references: msg.references as DocumentReference[] | undefined,
          timestamp: msg.created_at ? new Date(msg.created_at) : undefined,
          metadata: msg.metadata as Record<string, unknown> | undefined,
        }));

        setLegacyMessages(chatMessages);

        // Auto-scroll only when user is near bottom (read from DOM)
        const el = messagesContainerRef.current;
        if (el) {
          const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
          if (distanceFromBottom <= 100) {
            el.scrollTop = el.scrollHeight;
          }
        }

        const isIngestion =
          conversation.metadata?.type === 'basecamp-ingestion' ||
          conversation.metadata?.type === 'ingestion';
        const { isIngestionThreadComplete } = await import('@/lib/ingestion-streaming-flip');
        const isIngestionComplete = isIngestionThreadComplete({
          metadata: conversation.metadata,
          status: conversation.status,
          messages: conversation.messages,
        });

        if (isIngestion && isIngestionComplete) {
          if (setThinkingModeActive) setThinkingModeActive(false);
          if (setIsTyping) setIsTyping(false);
          window.dispatchEvent(
            new CustomEvent('thinking-mode-event', { detail: { type: 'thinking_complete' } }),
          );
          const { maybeDispatchIngestionComplete } = await import(
            '@/lib/basecamp-ingestion-events'
          );
          maybeDispatchIngestionComplete(
            conversation,
            conversationId,
            dispatchedIngestionCompleteRef.current,
          );
          setHistoryRefreshKey?.((k) => k + 1);
          console.log('[ChatPane] Ingestion complete, stopping polling');
          stopPolling();
        }
      } catch (error) {
        consecutiveErrors++;
        const status = (error as any)?.status;
        if (status === 404 || status === 410) {
          // Permanent — conversation deleted or never existed. Stop immediately.
          console.warn('[ChatPane] Polling stopped: conversation not found (', status, '):', conversationId);
          stopPolling();
          return;
        }
        if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
          console.warn('[ChatPane] Polling stopped after', MAX_CONSECUTIVE_ERRORS, 'consecutive errors:', conversationId);
          stopPolling();
          return;
        }
        console.error('[ChatPane] Polling error (', consecutiveErrors, '/', MAX_CONSECUTIVE_ERRORS, '):', error);
      }
    }, interval);
  }, [setIsPolling, pollingIntervalRef, messagesContainerRef, setLegacyMessages, isNearBottom, userHasScrolled, stopPolling, setThinkingModeActive, setIsTyping, setHistoryRefreshKey]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  return {
    startPolling,
    stopPolling,
  }
}

